/**
 * PatientConsentCard — R5 read-only "Additional Options" summary card for the
 * proband (patient). Mirrors the comparator's consent card (see
 * `ComparatorDetailView`): it shows only the org-applicable option rows and
 * exposes an Edit button + clickable surface that opens `<PatientConsentModal>`.
 *
 * These are optional lab programs (incidental findings, research contact, NY
 * sample retention) — not the patient's full genetic-testing consent, which is
 * completed separately in the Inflection Family app.
 *
 * Presentation-only: it reads the canonical patient's three values and emits an
 * edit intent. Editing happens in the modal (which reuses the existing
 * `<ProbandConsentSections>` controls). The legacy single-column layout keeps
 * the inline editable cards; only the R5 side-nav layout uses this summary.
 */

import { FormCard } from './FormCard'
import type { ComparatorParentConsentConfigSlice } from './comparatorParentConsentConfig'
import { comparatorConsentAnyApplicable } from './comparatorParentConsentConfig'
import type { CanonicalPatient } from '@/ordering/canonicalOrder'

interface PatientConsentCardProps {
  configs: ComparatorParentConsentConfigSlice
  patient: CanonicalPatient | null | undefined
  /**
   * Edit intent. Omit (or pass `undefined`) to render a read-only card: no Edit
   * button and no clickable surface (e.g. a submitted order the user can't edit).
   */
  onEdit?: () => void
  /** Highlight (orange) when a required incidental-findings choice is still missing. */
  needsAttention?: boolean
  /** Show the purple "required" accent bar even when complete. */
  required?: boolean
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium uppercase tracking-wide text-text-body-subtle dark:text-gray-500">
        {label}
      </span>
      <span className="text-sm text-text-body dark:text-gray-400">{value || '—'}</span>
    </div>
  )
}

function incidentalLabel(value: string | null | undefined): string {
  if (value === 'opt-in') return 'Opt in'
  if (value === 'opt-out') return 'Opt out'
  return 'Not provided'
}

function yesNo(value: boolean | null | undefined): string {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return 'Not provided'
}

export function PatientConsentCard({
  configs,
  patient,
  onEdit,
  needsAttention = false,
  required = false,
}: PatientConsentCardProps) {
  const anyApplicable = comparatorConsentAnyApplicable(configs)

  return (
    <FormCard
      title="Additional Options"
      onEdit={onEdit}
      clickableSurface
      needsAttention={needsAttention}
      required={required}
      completed={!needsAttention}
    >
      {anyApplicable ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {configs.incidental?.isApplicable ? (
            <FieldRow
              label="Incidental findings"
              value={incidentalLabel(patient?.incidentalFindingsSelection)}
            />
          ) : null}
          {configs.research?.isApplicable ? (
            <FieldRow label="Research contact" value={yesNo(patient?.researchContactConsent)} />
          ) : null}
          {configs.ny?.isApplicable ? (
            <FieldRow
              label="NY sample retention"
              value={yesNo(patient?.newYorkSampleRetentionOptIn)}
            />
          ) : null}
        </div>
      ) : (
        <span className="text-sm text-text-body-subtle dark:text-gray-500">
          No additional options are available for this order.
        </span>
      )}
    </FormCard>
  )
}
